<template>
    <div class="grid-wrapper">
        <DxDataGrid ref="grid" :data-source="dxDataSource" @selection-changed="onSelectionChanged"
            @exporting="onExporting" v-bind="getDefaultGridProperties()">
            <DxSelection mode="single" />
            <DxHeaderFilter :visible="true" />
            <DxFilterRow :visible="true" />
            <DxFilterPanel :visible="true" />
            <DxPaging :enabled="true" :pageSize="10" />
            <DxPager :visible="true" :allowed-page-sizes="[10, 20, 50, 'all']" display-mode="full"
                :show-page-size-selector="true" :show-info="true" :show-navigation-buttons="true" />
            <DxRemoteOperations :filtering="true" :paging="true" :sorting="true" :summary="true" :grouping="true"
                :group-paging="true" />
            <DxExport :enabled="true" />
            <DxColumnChooser :enabled="true" />
            <DxToolbar>
                <DxItem location="after" :caption="$t('zonas.grid.toolbar.editar_zona')" template="btnEditarZona"
                    v-if="tienePermisoZonasEditar(autho)" />
                <DxItem location="after" :caption="$t('zonas.grid.toolbar.actualizar_rejilla')"
                    template="btnActualizarRejilla" />
                <DxItem location="after" :caption="$t('zonas.grid.toolbar.restaurar_rejilla')"
                    template="btnRestaurarRejilla" />
                <DxItem location="after" name="exportButton" />
                <DxItem location="after" name="columnChooserButton" />
            </DxToolbar>
            <DxColumn data-field="Nombre" :caption="$t('zonas.grid.nombre')" sortOrder="asc" :sortIndex="0">
            </DxColumn>
            <DxColumn data-field="NombreRegion" :caption="$t('zonas.grid.nombre_region')">
            </DxColumn>
            <DxColumn data-field="UsuarioJefe" :caption="$t('zonas.grid.usuario_jefe')">
            </DxColumn>
            <template #btnEditarZona>
                <DxButton :hint="$t('zonas.grid.toolbar.editar_zona')" icon="fa fa-pencil" styling-mode="text"
                    type="default" @click="editarZona" :disabled="!hayUnaZonaSeleccionada" />
            </template>
            <template #btnActualizarRejilla>
                <DxButton :hint="$t('zonas.grid.toolbar.actualizar_rejilla')" @click="refresh(grid!.instance!)"
                    icon="fa-solid fa-sync" styling-mode="text" type="default" />
            </template>
            <template #btnRestaurarRejilla>
                <DxButton :hint="$t('zonas.grid.toolbar.restaurar_rejilla')" @click="reset(grid!.instance!)"
                    icon="fa-solid fa-filter-slash" styling-mode="text" type="default" />
            </template>
        </DxDataGrid>
    </div>
</template>

<script setup lang="ts">
import {
    DxAsyncRule, DxColumn, DxDataGrid, DxEditing, DxEmailRule, DxFormItem, DxRemoteOperations, DxRequiredRule, DxLookup, DxHeaderFilter, DxFilterRow,
    DxFilterPanel, DxPaging, DxPager, DxCustomRule, DxToolbar, DxItem, DxPopup, DxSelection, DxExport, type DxDataGridTypes, DxColumnChooser
} from "devextreme-vue/data-grid";
import { computed, ref } from 'vue';
import { useIntl } from '@services/i18n';
import SaveZonaModal from "./SaveZonaModal.vue";
import { DxButton } from "devextreme-vue/button";
import { useApi } from '@/.shared/.api/integration';
// import type { ZonaDetalleModel } from '@/.shared/.api';
import type CustomStore from 'devextreme/data/custom_store';
import { useRouter } from '@/.shared/services/routing-services';
import { useModalService } from "@/.shared/services/modal-service";
import type { RowDblClickEvent, SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { refresh, reset, exportToExcel, getDefaultGridProperties, getSelectedRow } from "@/.shared/helpers/grid-helper";
import type { EditZonaModel, ZonaDetalleModel } from "@/.shared/.api";
import { tienePermisoZonasEditar } from "@/.shared/helpers/permission-helper";
import { useAuthorization } from "@/.shared/services/auth/authorization";

const props = defineProps<{ dxDataSourceProp: CustomStore | undefined }>();
const dxDataSource = computed(() => props.dxDataSourceProp);

const api = useApi();
const intl = useIntl();
const router = useRouter();
const modalService = useModalService();
const autho = useAuthorization();

const grid = ref<DxDataGrid>();
let zonaSeleccionada = ref<ZonaDetalleModel>();
let idZonaSeleccionada = computed(() => zonaSeleccionada.value?.Id); // computed = function que devuelve algo. watch = function que no devuelve nada (void).
let hayUnaZonaSeleccionada = computed(() => zonaSeleccionada.value != null);

function onSelectionChanged(e: SelectionChangedEvent): void {
    // const idZonaSeleccionada = e.selectedRowKeys[0];
    zonaSeleccionada.value = e.selectedRowsData[0];
}

function onExporting(e: DxDataGridTypes.ExportingEvent): void {
    exportToExcel(e, 'Zonas');
}

async function editarZona(): Promise<void> {
    if (hayUnaZonaSeleccionada.value) {
        const zona = zonaSeleccionada.value!;
        const zonaEditModel: EditZonaModel = {
            Id: zona.Id,
            Nombre: zona.Nombre,
            IdRegion: zona.IdRegion,
            IdUsuarioJefe: zona.IdUsuarioJefe
        };
        const result = await modalService.showModal(SaveZonaModal, { zonaProp: zonaEditModel }, { title: intl.i18n.t('zonas.grid.boton_editar_zona'), maxWidth: "600", maxHeight: "70vh" });
        if (result) {
            const dxDataGrid = grid.value!.instance!;
            await refresh(dxDataGrid);
            const zona = await getSelectedRow(dxDataGrid) as ZonaDetalleModel;
            zonaSeleccionada.value = zona;
        }
    }
}
</script>